import React, { useState } from "react";
import { Reveal } from "./Ornament";

const SERVICES = [
  {
    name: "Entertainment Direction",
    desc: "We design the complete entertainment journey of your event, from the first creative idea to the final encore, ensuring every performance feels intentional, elegant and unforgettable.",
    image: "/generated/golden-performer-1.jpg",
  },
  {
    name: "Signature Performances",
    desc: "Curated live entertainment including vocalists, musicians, dancers, aerial artists, specialty acts and immersive performances, selected to match the atmosphere of every celebration.",
    image: "/generated/act-aerial-duo.jpg",
  },
  {
    name: "Creative Event Concepts",
    desc: "Original event concepts built around stories, cultures, fashion, cinema, music and artistic inspiration. Every celebration becomes its own unique world.",
    image: "/generated/concept-fly-me-to-the-moon.jpg",
  },
  {
    name: "Immersive Experiences",
    desc: "Multi-sensory experiences combining performance, lighting, staging, visual storytelling and audience interaction to create moments guests truly remember.",
    image: "/generated/immersive-1.png",
  },
  {
    name: "Luxury Wedding Entertainment",
    desc: "Elegant entertainment direction for destination weddings, welcome parties, ceremonies, receptions and after parties, designed as one seamless experience.",
    image: "/generated/concept-lake-doxa.jpg",
  },
  {
    name: "Hotel & Resort Entertainment",
    desc: "Premium entertainment concepts created for luxury hotels and resorts, from seasonal productions and guest experiences to signature events and exclusive evenings.",
    image: "/generated/arabian-nights-2.jpg",
  },
  {
    name: "Brand Activations",
    desc: "Creative entertainment for luxury brands, product launches, fashion presentations, VIP events and experiential marketing activations.",
    image: "/generated/lipton-sunset-arch.png",
  },
  {
    name: "Corporate Entertainment",
    desc: "Sophisticated entertainment concepts for galas, award ceremonies, conferences, incentive events and executive celebrations.",
    image: "/generated/white-party-1.jpg",
  },
  {
    name: "Destination Experiences",
    desc: "Entertainment and immersive concepts created for yachts, villas, private estates and exceptional destinations across Greece and internationally.",
    image: "/generated/sea-nymphs-yacht-hero.jpg",
  },
  {
    name: "Creative Styling & Costumes",
    desc: "Bespoke costume design, artistic styling and visual identity developed exclusively for each concept and performance.",
    image: "/generated/tribal-show.jpg",
  },
  {
    name: "Technology & Digital Acts",
    desc: "Projection mapping, LED performances, interactive installations and digital experiences that combine technology with artistic storytelling.",
    image: "/generated/immersive-3.png",
  },
  {
    name: "Production & Coordination",
    desc: "Complete artistic coordination including performers, rehearsals, technical production, scheduling and on-site direction, delivered through one experienced creative team.",
    image: "/generated/act-live-music-band.jpg",
  },
];

/** Editorial "sticky image + dimmed list" services block — inspired by
 *  producer-entertainment.vercel.app services layout. */
export default function ServicesList() {
  const [activeIdx, setActiveIdx] = useState(0);
  return (
    <section
      id="services-list"
      className="relative bg-[var(--lu-black)] section-y grain"
      data-testid="services-list-section"
    >
      <div className="container-lux">
        {/* Header row */}
        <div className="grid items-end gap-8 lg:grid-cols-[1fr_minmax(0,26rem)] mb-16">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-[var(--lu-gold)]" aria-hidden />
              <p className="kicker">Our Services</p>
            </div>
            <Reveal>
              <h2 className="mt-6 h-editorial text-[46px] sm:text-[60px] lg:text-[88px] text-[var(--lu-ivory)] leading-[0.98]">
                <span className="block">One team.</span>
                <span className="block">All night<span className="text-[var(--lu-gold)]">, in one hand.</span>
                </span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={2}>
            <p className="text-[17px] leading-[1.85] text-ivory-75 font-light">From the first concept to the last applause, every element of it
              entertainment and production is designed, staffed and directed by
              our team — never handed-off, never rushed.</p>
          </Reveal>
        </div>

        {/* List + sticky poster */}
        <div className="grid gap-10 lg:grid-cols-[1fr_minmax(0,30rem)] lg:gap-16">
          {/* LEFT: dimmed list */}
          <ul className="border-t border-[var(--lu-gold)]/25">
            {SERVICES.map((s, i) => {
              const active = activeIdx === i;
              return (
                <li
                  key={s.name}
                  className="group border-b border-[var(--lu-gold)]/25"
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`services-list-item-${i}`}
                >
                  <div className="py-7 md:py-8">
                    <div
                      className="transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-3"
                    >
                      <h3
                        className={`font-serif text-2xl leading-tight transition-colors duration-500 md:text-4xl ${
                          active ? "text-[var(--lu-ivory)]" : "text-ivory-40"
                        }`}
                      >
                        {s.name}
                      </h3>
                      <p
                        className={`mt-2 text-[14px] leading-relaxed transition-opacity duration-500 font-light ${
                          active ? "text-ivory-75" : "text-ivory-40"
                        }`}
                      >
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
            <li className="border-b border-[var(--lu-gold)]/25 py-7">
              <span className="font-serif italic text-[24px] text-[var(--lu-gold)]/70">&mdash; and whatever else your evening calls for.</span>
            </li>
          </ul>

          {/* RIGHT: sticky poster (desktop) */}
          <div className="hidden lg:block">
            <div className="sticky top-32 aspect-[4/5] overflow-hidden rounded-sm border border-[var(--lu-gold)]/40 shadow-[var(--shadow-lux)]">
              {SERVICES.map((s, i) => (
                <img
                  key={s.name}
                  src={s.image}
                  alt={s.name}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    i === activeIdx ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ boxShadow: "inset 0 0 90px 8px rgba(0,0,0,0.5)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--lu-black)]/70 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-end p-7">
                <p
                  className="font-serif text-[28px] text-[var(--lu-ivory)]"
                  style={{ textShadow: "0 2px 14px rgba(0,0,0,0.7)" }}
                  data-testid="services-list-poster-title"
                >
                  {SERVICES[activeIdx].name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
